import { EmployeeCompensationLineFormValue } from "@/pages/employees/employee/components/employee-compensation-form.types";
import {
  getHireStepSections,
  HireFieldValues,
  HireOnboardingValues,
  HirePackageState,
} from "@/pages/employees/hire/hire-form";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { PrefillCandidate } from "@/types/employees/employee-hire.types";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { ApiHiringFlow } from "@/types/hiring-flow.types";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { createContext, Dispatch, SetStateAction, useContext } from "react";
import { useTranslation } from "react-i18next";

export type HireJobState = "idle" | "pending" | "unknown";

export enum HireStep {
  create = "create",
  personal = "personal",
  onboarding = "onboarding",
  final = "final",
}

export const useCanLaunchHireWorkflows = (): boolean => {
  const { canIEdit, isFeatureEnabled } = usePermissions();
  return canIEdit(ApiPermission.workflows_manual_run) && isFeatureEnabled(ApiFeatureType.workflows);
};

export const getHireSteps = (
  flow: ApiHiringFlow,
  personnelNumberAuto: boolean | undefined
): HireStep[] => [
  HireStep.create,
  ...(getHireStepSections(flow, "personal", personnelNumberAuto).length ? [HireStep.personal] : []),
  ...(flow.probation_is_enabled || flow.workflows_is_enabled ? [HireStep.onboarding] : []),
];

export const useHireSteps = (): HireStep[] => useHireContext().steps;

export const useHireStepLabels = (): Partial<Record<HireStep, string>> => {
  const { t } = useTranslation();
  return {
    [HireStep.create]: t("core:create_teammate"),
    [HireStep.personal]: t("core:personal_details"),
    [HireStep.onboarding]: t("core:onboarding_setup"),
  };
};

export interface HireContextValue {
  employee?: ApiEmployee;
  flow: ApiHiringFlow;
  mainValues: HireFieldValues;
  personalValues: HireFieldValues;
  onboarding: HireOnboardingValues;
  setMainValues: Dispatch<SetStateAction<HireFieldValues>>;
  setPersonalValues: Dispatch<SetStateAction<HireFieldValues>>;
  setOnboarding: Dispatch<SetStateAction<HireOnboardingValues>>;
  compensation: HirePackageState;
  setCompensation: (value: HirePackageState) => void;
  compensationDefaults: Partial<EmployeeCompensationLineFormValue>;
  defaultCurrency: string;
  step: HireStep;
  steps: HireStep[];
  goToStep: (step: HireStep) => void;
  personnelNumberAuto: boolean | undefined;
  isPreview: boolean;
  isSubmitting: boolean;
  hireJobState: HireJobState;
  complete: (
    values: HireOnboardingValues,
    fieldValues?: Partial<Record<"main" | "personal", HireFieldValues>>
  ) => Promise<void>;
  errors: Partial<Record<HireStep, Record<string, string>>>;
  prefillData?: PrefillCandidate;
  candidateId?: number;
  exit: () => void;
}

export const HireContext = createContext<HireContextValue | null>(null);

export const useHireContext = () => {
  const context = useContext(HireContext);
  if (!context) {
    throw new Error("useHireContext must be used within HireProvider");
  }
  return context;
};
